import { useAuth } from '@agentnet/auth';
import { useFeatureFlag, useFeatureFlagContext } from '@agentnet/feature-flags';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Footer from 'core/footer/footer';
import IdlePrompter from 'core/IdlePrompter';
import ProductFruitsFullStory from 'core/layout-core/core-content/ProductFruitsFullStory';
import LeftNav from 'core/navbar/LeftNav';
import { OrderManagementInvoices, RemittanceReceivables, VerifiedOutlined } from 'core/navbar/LeftNavIcons';
import {
  AccountingLinks,
  AdminLinks,
  DashboardLinks,
  FilesLinks,
  getFincenLinks,
  OrderManagementInfoPanelLinks,
  OrderManagementLinks,
  ReportsLinks,
  ServicesLinks,
} from 'core/navbar/LeftNavLinks';
import NavFileInfo from 'features/files/FileSnapshotLeftNav';
import HelpLayout from 'features/help/components/Main/HelpLayout/HelpLayout';
import { ContentfulErrorProvider } from 'features/help/hooks/ContentfulErrorContext';
import DefaultOrderManagementPanel from 'features/orderManagement/DefaultOrderManagementPanel';
import OrderInfoPanel from 'features/orderManagement/OrderInfoPanel';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useNavPermissions } from 'hooks/useNavPermissions';
import { useViewState } from 'hooks/ViewStateContext';
import { DefaultNewFile } from 'models/file-model';
import { useContext, useEffect, useMemo } from 'react';
import { Route, useHistory } from 'react-router-dom';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import GlobalHeader, { CurrentView } from '../header/header';
import { NavLinkType } from '../navbar/NavLinkType';
import { RouteType, Layout } from './Router';
import { NavLinkText } from 'core/navbar/NavLinkTexts';

// A wrapper for <Route> that handles "sub"-routes by passing them as
// `routes` prop to the component it renders.

const RouteWithSubRoutes = (route: RouteType): JSX.Element => {
  const auth = useAuth();
  const useStyles = makeStyles(() =>
    createStyles({
      routeRoot: {
        display: 'flex',
        flex: `1 0 auto`,
        backgroundColor: !route.isNested ? '#FAFAFA' : 'transparent',
        width: '100%',
        height: 'auto',
      },
      loader: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh', // Full viewport height
        width: '100vw', // Full viewport width
        position: 'fixed', // Optional: Use if you want the loader to be fixed on the screen
      },
    }),
  );
  const classes = useStyles();
  const history = useHistory();
  const isAuthenticated = auth.isAuthenticated();
  const isFincenReportingNavEnabled = useFeatureFlag(FeatureFlag.SHOW_FINCEN_REPORTING_NAVIGATION, false);

  const addFincenReportingLink = (navState: NavLinkType[]) => {
    if (isFincenReportingNavEnabled?.value) {
      navState.push({
        linkText: 'FinCEN Real Estate Report',
        path: '/files/:fileId/fincen-reporting',
        icon: <VerifiedOutlined />,
        expanded: false,
        testId: 'GlobalNavFilesFincenReporting',
      });
    }
    return navState;
  };

  useEffect(() => {
    if (!route.isPublic && !auth.isAuthenticated()) {
      auth.login().then();
    }
  }, []);

  const {
    isContextSet: isFeatureFlagContextSet,
    setUserContext: setFeatureFlagContext,
    clearUserContext: clearFeatureFlagContext,
  } = useFeatureFlagContext();

  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};

  const { userFirm, profileStatus, profile, isReactivating } = profileCtx;

  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { useShouldShowSummary } = fincenReportCtx ?? {};
  const shouldShowSummary = useShouldShowSummary?.() ?? false;

  const { viewState, setViewState, menuCollapsed, setMenuCollapse, navState, setNavState } = useViewState();
  const {
    hasPermission,
    prismUserProfile,
    fileData,
    setFileData,
    setRatesAndFeeDrawerClicked,
    isFormDirty,
    setOpenRatesAndFeeDrawer,
    openRatesAndFeeDrawer,
    setOpenSaveModal,
  } = useNavPermissions();

  // TODO: Fix the router. This is a hack because the router is not working appropriately.
  // Helper function to merge nav states and remove non-existing links
  const mergeNavStates = (existingNavState: NavLinkType[], newNavState: NavLinkType[]) => {
    const mergedState = existingNavState.filter((existingLink) =>
      newNavState.some((newLink) => newLink.path === existingLink.path),
    );

    newNavState.forEach((newLink) => {
      const existingLinkIndex = mergedState.findIndex((link) => link.path === newLink.path);
      if (existingLinkIndex !== -1) {
        mergedState[existingLinkIndex] = {
          ...mergedState[existingLinkIndex],
          ...newLink,
          expanded: mergedState[existingLinkIndex].expanded,
        };
      } else {
        mergedState.push(newLink);
      }
    });

    return mergedState;
  };

  useEffect(() => {
    // eslint-disable-next-line require-await
    const bootstrapLaunchDarklyAsync = async () => {
      if (isAuthenticated && userFirm?.firmId && profileStatus === 'success') {
        if (!isFeatureFlagContextSet) {
          setFeatureFlagContext({ ...profile, firmId: userFirm.firmId });
        }
      } else {
        clearFeatureFlagContext();
      }
    };
    bootstrapLaunchDarklyAsync();
  }, [
    clearFeatureFlagContext,
    isAuthenticated,
    setFeatureFlagContext,
    isFeatureFlagContextSet,
    userFirm?.firmId,
    profileStatus,
    profile,
  ]);

  // TODO: Unwind all of this once the router is fixed.
  useEffect(() => {
    if (!route.path.includes('/files')) {
      window.sessionStorage.removeItem('fileId');
      window.sessionStorage.removeItem('properties');
      if (fileData?.fileId) setFileData?.(DefaultNewFile);
    }
    const pathname = route.location.pathname;
    let newNavState: NavLinkType[] = [];

    // Logic to determine the nav state based on the pathname
    switch (true) {
      case pathname.startsWith('/order-management'):
        if (route.location.state) {
          newNavState = OrderManagementInfoPanelLinks;
        } else {
          newNavState = OrderManagementLinks;
          const remittanceReceivablesLink: NavLinkType = {
            linkText: 'Remittance Receivables',
            path: '/order-management/remittance-receivables',
            icon: <RemittanceReceivables />,
            expanded: false,
            testId: 'RemittanceReceivablesTestLink',
          };
          newNavState.push(remittanceReceivablesLink);
          const invoicesDashboardFscLink: NavLinkType = {
            linkText: NavLinkText.Invoices,
            path: '/order-management/invoices',
            icon: <OrderManagementInvoices />,
            expanded: false,
            testId: 'InvoicesTestLink',
          };
          newNavState.push(invoicesDashboardFscLink);
        }
        break;

      case pathname.startsWith('/files') && pathname.includes('/fincen-reporting'):
        newNavState = getFincenLinks(shouldShowSummary);
        break;
      case pathname.startsWith('/files'):
        newNavState = addFincenReportingLink([...FilesLinks]);
        break;
      case pathname.startsWith('/accounting') || pathname.startsWith('/invoices') || pathname.startsWith('/statements'):
        newNavState = AccountingLinks;
        break;
      case pathname.startsWith('/reporting'):
        newNavState = ReportsLinks;
        break;
      case pathname.startsWith('/services'):
        newNavState = ServicesLinks;
        break;
      case pathname.startsWith('/dashboards'):
        newNavState = DashboardLinks;
        break;
      case pathname.startsWith('/admin'):
        newNavState = AdminLinks;
        break;

      default:
        newNavState = addFincenReportingLink([...FilesLinks]);
    }

    // ANS-6600: Filter navigation links based on Approved Attorney permissions
    newNavState = newNavState.filter((link) => {
      const linkText = link.linkText;

      // Files menu - block specific items
      if (linkText === NavLinkText.BackTitle && !hasPermission.backtitle) return false;
      if (linkText === NavLinkText.CPL && !hasPermission.cpl) return false;
      if (linkText === NavLinkText.ServiceOrders && !hasPermission.serviceOrders) return false;
      if (linkText === NavLinkText.Underwriting && !hasPermission.underwriting) return false;
      if (linkText === NavLinkText.Jackets && !hasPermission.jackets) return false;
      if (linkText === NavLinkText.RatesFees && !hasPermission.ratesAndFees) return false;

      // Accounting menu - block specific items
      if (linkText === NavLinkText.ReportPay && !hasPermission.accounting) return false;
      if (linkText === NavLinkText.ServiceOrderInvoices && !hasPermission.serviceOrderInvoices) return false;
      if (linkText === NavLinkText.Statements && !hasPermission.statements) return false;

      // Services menu - block specific items
      if (linkText === NavLinkText.AgencyVerificationLetters && !hasPermission.agencyVerificationLetters) return false;
      if (linkText === NavLinkText.BackTitleSearch && !hasPermission.canRunBackTitleAdHoc) return false;
      if (linkText === NavLinkText.PropertyReports && !hasPermission.propertyReports) return false;
      if (linkText === NavLinkText.PolicyUpload && !hasPermission.canRunPolicyUpload) return false;

      // Reports menu - block entire menu
      if (linkText === NavLinkText.Reports && !hasPermission.reports) return false;

      // Dashboard menu - block specific items
      if (linkText === NavLinkText.ServiceOrders && pathname.startsWith('/dashboards') && !hasPermission.dashboard)
        return false;

      // Admin menu - block specific items
      if (linkText === NavLinkText.AddressBook && !hasPermission.addressBook) return false;
      if (linkText === NavLinkText.AutoReporting && !hasPermission.autoReporting) return false;
      if (linkText === NavLinkText.ESignatures && !hasPermission.eSignatures) return false;
      if (pathname.startsWith('/admin') && linkText === NavLinkText.Invoices && !hasPermission.invoiceDashboard)
        return false;

      return true;
    });

    setNavState(mergeNavStates(navState, newNavState));
  }, [route.path, route.location.state, isFincenReportingNavEnabled, shouldShowSummary, hasPermission]);

  // Stable key tied to firmId to force remount when firm changes
  const firmComponentKey = useMemo(() => userFirm?.firmId?.toString() ?? 'no-firm', [userFirm?.firmId]);

  const onViewSwitchAdmin = () => {
    setViewState(CurrentView.AdminUser);
    history.push('/admin/user/profile');
  };

  const onViewSwitchPortal = () => {
    setViewState(CurrentView.Portal);
    history.push('/files');
  };

  return (
    <>
      {(auth.isAuthenticated() || route.isPublic) && (
        <>
          <ProductFruitsFullStory />
          <IdlePrompter />
          <Route
            key={firmComponentKey}
            path={route.path}
            exact={route.exact}
            render={(props) => {
              // pass the sub-routes down to keep nesting
              if (route.layout === Layout.AuthLayout) {
                return (
                  <div className={clsx(classes.routeRoot, 'routeRoot')}>
                    <GlobalHeader
                      onViewSwitch={onViewSwitchAdmin}
                      onPortalSwitch={onViewSwitchPortal}
                      currentView={viewState}
                      hasPermission={hasPermission}
                      hideNav={!hasPermission.hasFirmId}
                    />
                    {route.location.pathname !== '/files' &&
                      route.location.pathname !== '/searchFirms' &&
                      (hasPermission.hasFirmId || hasPermission.accessOrderManagement) && (
                        <LeftNav
                          menuCollapsed={menuCollapsed}
                          setMenuCollapse={setMenuCollapse}
                          navState={navState}
                          setNavState={setNavState}
                          pathParams={route}
                          hasPermission={hasPermission}
                          userFirm={userFirm}
                          prismUserProfile={prismUserProfile}
                          fileData={fileData}
                          openRatesAndFeeDrawer={openRatesAndFeeDrawer}
                          setRatesAndFeeDrawerClicked={setRatesAndFeeDrawerClicked}
                          setOpenRatesAndFeeDrawer={setOpenRatesAndFeeDrawer}
                          isFormDirty={isFormDirty}
                          setOpenSaveModal={setOpenSaveModal}
                          {...props}
                        >
                          {route.location.pathname.startsWith('/files') && <NavFileInfo />}
                          {hasPermission.accessOrderManagement &&
                            (route.location.pathname === '/order-management' ||
                              route.location.pathname === '/order-management/closed-order-details') &&
                            (route.location.state == null ? <DefaultOrderManagementPanel /> : <OrderInfoPanel />)}
                        </LeftNav>
                      )}

                    {!userFirm?.firmId && (profileStatus !== 'success' || isReactivating) ? (
                      <div className={classes.loader}>
                        <LoadingSpinner
                          variant="circle"
                          status={isReactivating ? 'pending' : profileStatus}
                          label={'Loading'}
                        />
                      </div>
                    ) : (
                      <route.component {...props} routes={route.routes} isNested={route.isNested} />
                    )}
                  </div>
                );
              } else if (route.layout === Layout.PublicLayout) {
                return (
                  <>
                    <GlobalHeader currentView={CurrentView.Public} hideNav />
                    <div className={clsx(classes.routeRoot, 'routeRoot')}>
                      <route.component {...props} routes={route.routes} isNested={route.isNested} />
                    </div>
                    <Footer />
                  </>
                );
              } else if (route.layout === Layout.UserNotFoundLayout) {
                return (
                  <>
                    <GlobalHeader currentView={CurrentView.AccountNotFound} hideNav />
                    <div className={clsx(classes.routeRoot, 'routeRoot')}>
                      <route.component {...props} routes={route.routes} isNested={route.isNested} />
                    </div>
                  </>
                );
              } else if (route.layout === Layout.HelpLayout) {
                return (
                  <ContentfulErrorProvider>
                    <HelpLayout>
                      <div className={clsx(classes.routeRoot, 'routeRoot')}>
                        <route.component {...props} routes={route.routes} isNested={route.isNested} />
                      </div>
                    </HelpLayout>
                  </ContentfulErrorProvider>
                );
              }
              return (
                <div className={clsx(classes.routeRoot, 'routeRoot')}>
                  <route.component {...props} routes={route.routes} isNested={route.isNested} />
                </div>
              );
            }}
          />
        </>
      )}
    </>
  );
};

export default RouteWithSubRoutes;
